import React, { Fragment } from 'react';
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
  DataTableToolbar
} from '@cdk-uip/react-data-table';
import { Dialog, DialogBody, DialogFooterButton } from '@cdk-uip/react-dialog';
import { Card } from '@cdk-uip/react-card';
import Spinner from '../../../../common/Spinner';
import { getApolloClient } from '../../../../../containers/GraphQLClient';
import gql from 'graphql-tag';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import EntityWithAvatar from '../../../../common/EntityWithAvatar';
import SubscriptionStatus from '../../../../common/subscription-status/SubscriptionStatus';
import SearchComponent from '../../../../common/SearchComponent';
import EmptyContent from '../../../../common/empty-content';
import { FortellisConstants } from '../../../../common/FortellisConstants';
import SolutionTypeURLMapperUtil from '../../../../common/SolutionTypeURLMapperUtil';
import { withRouter } from 'react-router-dom';
import LearnMore from '../../../../common/LearnMore/LearnMore';
import { environmentURLs } from '../../../../common/environment/CaptureEnvironment';
import { Button } from '@cdk-uip/react-button';
import { Menu, MenuAnchor, MenuItem } from '@cdk-uip/react-menu';
import { IconButton } from '@cdk-uip/react-icon-button';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50];

class Subscriptions extends React.Component {
  state = {
    solSubscriptions: [],
    searchText: '',
    entity: null,
    isDialogueOpen: false,
    currentPage: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    pageSizeMenuOpen: false
  };

  componentDidMount() {
    this.fetchSolutionSubscriptions();
  }

  fetchSolutionSubscriptions = async () => {
    const client = getApolloClient();
    this.setState({ loading: true });
    let getSolutionSubscriptionsResponse = await client.query({
      query: getSolutionSubscriptions,
      variables: {
        solutionId: this.props.solutionId
      }
    });
    getSolutionSubscriptionsResponse.data.getSolutionSubscriptions.forEach(
      subscription => {
        if (!subscription.solution) {
          subscription.solution = {
            overview: { version: FortellisConstants.NotAvailable }
          };
          subscription.plan = {
            id: FortellisConstants.NotAvailable,
            planName: FortellisConstants.NotAvailable
          };
        }
      }
    );
    if (
      getSolutionSubscriptionsResponse &&
      getSolutionSubscriptionsResponse.errors
    ) {
      this.setState({ errorFetchingSubscriptions: true });
    } else if (
      getSolutionSubscriptionsResponse &&
      getSolutionSubscriptionsResponse.data &&
      getSolutionSubscriptionsResponse.data.getSolutionSubscriptions
    ) {
      let entityIds = [];
      if (
        Array.isArray(
          getSolutionSubscriptionsResponse.data.getSolutionSubscriptions
        )
      ) {
        entityIds = getSolutionSubscriptionsResponse.data.getSolutionSubscriptions.map(
          item => item.entityId
        );
      }
      await this.getEntityNames(entityIds);

      // Build enriched subscriptions using a new array (avoid mutating Apollo cache)
      const enriched = getSolutionSubscriptionsResponse.data.getSolutionSubscriptions.map(
        subscription => {
          const match =
            this.state.entity &&
            this.state.entity.find(e => e.id === subscription.entityId);
          return match
            ? { ...subscription, entityName: match.name }
            : { ...subscription };
        }
      );

      this.setState({ solSubscriptions: enriched });
    }
    this.setState({ loading: false });
  };

  getEntityNames = async entityIds => {
    // Deduplicate entityIds to avoid redundant API calls
    const uniqueIds = [...new Set(entityIds)];

    let chunks = [];
    let chunkSize = 200;
    for (let i = 0; i < uniqueIds.length; i += chunkSize) {
      chunks.push(uniqueIds.slice(i, i + chunkSize));
    }

    const client = getApolloClient();
    let entityData = [];

    // Sequential fetching instead of Promise.all to avoid memory spike
    for (let i = 0; i < chunks.length; i++) {
      const res = await client.query({
        query: getEntitiesQuery,
        variables: { id: chunks[i] }
      });
      if (res.data && res.data.subscribedEntitiesFromPlatfom) {
        entityData = [...entityData, ...res.data.subscribedEntitiesFromPlatfom];
      }
    }

    this.setState({ entity: entityData.length ? entityData : null });
    return null;
  };

  onSearchChange = val => {
    this.setState({ searchText: val, currentPage: 0 });
  };

  onPageSizeMenuSelect = selected => {
    const selectedPageSize = PAGE_SIZE_OPTIONS[selected] || DEFAULT_PAGE_SIZE;
    this.setState({
      pageSize: selectedPageSize,
      currentPage: 0,
      pageSizeMenuOpen: false
    });
  };

  onEntityReception = entity => {
    let subcriptionCopy = JSON.parse(
      JSON.stringify(this.state.solSubscriptions)
    );
    subcriptionCopy.forEach(subscription => {
      if (subscription.entityId === entity.id) {
        Object.assign(subscription, { entityName: entity.name });
      }
    });
    this.setState({ solSubscriptions: subcriptionCopy });
  };

  onClose = () => {
    this.setState({ isDialogueOpen: false });
  };

  ActivationsMessage = () => {
    return (
      <Fragment>
        <FormattedMessage
          id="Activations.helpText"
          defaultMessage={`Review the current API activations and activate or deactivate their corresponding API connections.
        Use the Search to find specific activations.`}
        />
        &nbsp;
        <LearnMore
          url={`${
            environmentURLs.docs
          }docs/marketplace/marketplace-for-apps/activating-apps/`}
        />
        <br />
        <br />
      </Fragment>
    );
  };

  SubscriptionsMessage = () => {
    return (
      <Fragment>
        <FormattedMessage
          id="Subscriptions.helpText"
          defaultMessage={`Review the current API subscriptions and subscribe or unsubscribe their corresponding API connections.
            Use the Search and filter options to find specific subscriptions.`}
        />
        &nbsp;
        <LearnMore
          url={`${
            environmentURLs.docs
          }docs/marketplace/marketplace-for-apps/activating-apps/`}
        />
        <br />
        <br />
      </Fragment>
    );
  };

  render() {
    const {
      solSubscriptions,
      searchText,
      entity,
      currentPage,
      pageSize,
      pageSizeMenuOpen
    } = this.state;
    const { solution } = this.props;

    // Spread into new array before sort to avoid mutating state directly
    let subscriptionsToDisplay = [...solSubscriptions];
    let entityDetails = entity;

    subscriptionsToDisplay.sort((a, b) => {
      return a.status.localeCompare(b.status);
    });

    if (solSubscriptions && solSubscriptions.length && searchText) {
      let splittedSearchValues = searchText
        .toString()
        .toLowerCase()
        .trim()
        .split(' ');
      splittedSearchValues.forEach(searchText => {
        subscriptionsToDisplay = subscriptionsToDisplay.filter(
          subscription =>
            subscription.status.toLowerCase().includes(searchText) ||
            (subscription.entityName &&
              subscription.entityName.toLowerCase().includes(searchText)) ||
            (subscription.plan &&
              subscription.plan.planName &&
              subscription.plan.planName.toLowerCase().includes(searchText))
        );
      });
    }

    // Paginate: only render pageSize rows at a time
    const totalPages = Math.ceil(subscriptionsToDisplay.length / pageSize);
    const paginatedSubscriptions = subscriptionsToDisplay.slice(
      currentPage * pageSize,
      (currentPage + 1) * pageSize
    );

    return (
      <div>
        <div className="api-info-subtitle">
          {solution.activateOnly === true
            ? this.ActivationsMessage()
            : this.SubscriptionsMessage()}
        </div>
        <Card className="sol-managemnt-subscriptions_table">
          <Dialog open={this.state.isDialogueOpen} onCancel={this.onClose}>
            <DialogBody className=" textAlign-center">
              This is a Test Subscription! <br />
              You cannot perform any action on this subscription.
            </DialogBody>
            <DialogFooterButton className="dialogbtn-txt" cancel>
              <span>Okay</span>
            </DialogFooterButton>
          </Dialog>
          <DataTableToolbar>
            <div className="lead-search-parent">
              <SearchComponent
                className="lead-search"
                label="Search"
                onChange={this.onSearchChange}
              />
            </div>
          </DataTableToolbar>
          {this.state.loading ? (
            <div>
              <Spinner />
            </div>
          ) : paginatedSubscriptions && paginatedSubscriptions.length > 0 ? (
            <>
              <div className="sol-management-table">
                <DataTable>
                  <DataTableHeader>
                    <DataTableRow>
                      <DataTableHeaderCell
                        className="sol-managemnt-subscriptions_table-header-text textAlign-left"
                        nonNumeric
                      >
                        <FormattedMessage
                          id="DeveloperSubscriptions.table.column1"
                          defaultMessage="Entity"
                        />
                      </DataTableHeaderCell>
                      <DataTableHeaderCell
                        className="sol-managemnt-subscriptions_table-header-text textAlign-center"
                        nonNumeric
                      >
                        <FormattedMessage
                          id="DeveloperSubscriptions.table.column2"
                          defaultMessage="Plan"
                        />
                      </DataTableHeaderCell>
                      <DataTableHeaderCell
                        className="sol-managemnt-subscriptions_table-header-text textAlign-center"
                        nonNumeric
                      >
                        <FormattedMessage
                          id="DeveloperSubscriptions.table.column5"
                          defaultMessage="Payment Mode"
                        />
                      </DataTableHeaderCell>
                      <DataTableHeaderCell
                        className="sol-managemnt-subscriptions_table-header-text textAlign-center"
                        nonNumeric
                      >
                        {solution.activateOnly === false
                          ? 'Subscribed On'
                          : 'Activated On'}
                      </DataTableHeaderCell>
                      <DataTableHeaderCell
                        className="sol-managemnt-subscriptions_table-header-text textAlign-center"
                        nonNumeric
                      >
                        {solution.activateOnly === false
                          ? 'Subscription Status'
                          : 'Activation Status'}
                      </DataTableHeaderCell>
                    </DataTableRow>
                  </DataTableHeader>
                  <DataTableBody>
                    {paginatedSubscriptions.map(subscription => {
                      const solutionType =
                        subscription.solution &&
                        subscription.solution.solutionType
                          ? SolutionTypeURLMapperUtil.getSolutionTypeName(
                              subscription.solution.solutionType
                            )
                          : FortellisConstants.FORTELLIS_TYPE_URL;
                      return (
                        <DataTableRow
                          className="sol-managemnt-subscriptions-table-row"
                          key={subscription.subscriptionId}
                          onClick={() => {
                            if (
                              subscription?.entityId !== this.props?.entity?.id
                            ) {
                              this.props.history.push(
                                `/marketplace-account/mysolutions/${solutionType}/${
                                  subscription.solutionId
                                }/solution-management/manage/subscriptions/${
                                  subscription.subscriptionId
                                }/${subscription.entityId}`
                              );
                            } else {
                              this.setState({ isDialogueOpen: true });
                            }
                          }}
                        >
                          <DataTableCell className="nowrap sol-managemnt-subscriptions_table-body-text">
                            {entityDetails && (
                              <EntityWithAvatar
                                entityId={subscription.entityId}
                                entityDetails={entityDetails}
                                onEntityReception={this.onEntityReception}
                              />
                            )}
                          </DataTableCell>
                          <DataTableCell className="nowrap sol-managemnt-subscriptions_table-body-text textAlign-center">
                            {solution.activateOnly === false
                              ? subscription.plan.planName || 'N/A'
                              : 'N/A'}
                          </DataTableCell>
                          <DataTableCell className="nowrap sol-managemnt-subscriptions_table-body-text textAlign-center">
                            {subscription.paymentMode
                              ? subscription.paymentMode
                              : FortellisConstants.NotAvailable}
                          </DataTableCell>
                          <DataTableCell className="nowrap sol-managemnt-subscriptions_table-body-text textAlign-center">
                            {subscription.subscriptionStartDate
                              ? moment(
                                  subscription.subscriptionStartDate
                                ).format('DD MMM YYYY')
                              : FortellisConstants.NotAvailable}
                          </DataTableCell>
                          <DataTableCell className="nowrap sol-managemnt-subscriptions_table-body-text textAlign-center">
                            {subscription && subscription.status && (
                              <SubscriptionStatus subscription={subscription} />
                            )}
                          </DataTableCell>
                        </DataTableRow>
                      );
                    })}
                  </DataTableBody>
                </DataTable>
              </div>
              {subscriptionsToDisplay.length > DEFAULT_PAGE_SIZE && (
                <div className="sol-management-pagination">
                  <div className="sol-management-pagination-page-size">
                    <span>Rows per page</span>
                    <MenuAnchor className="sol-management-page-size-menu-anchor">
                      <Button
                        className="sol-management-page-size-button"
                        aria-label="Rows per page"
                        onClick={() =>
                          this.setState({ pageSizeMenuOpen: true })
                        }
                      >
                        {pageSize}
                        <IconButton>arrow_drop_down</IconButton>
                      </Button>
                      <Menu
                        open={pageSizeMenuOpen}
                        onCancel={() =>
                          this.setState({ pageSizeMenuOpen: false })
                        }
                        onSelected={this.onPageSizeMenuSelect}
                      >
                        {PAGE_SIZE_OPTIONS.map(size => (
                          <MenuItem key={size}>{size}</MenuItem>
                        ))}
                      </Menu>
                    </MenuAnchor>
                  </div>
                  <Button
                    className="sol-management-pagination-icon-button"
                    aria-label="First"
                    disabled={currentPage === 0}
                    onClick={() => this.setState({ currentPage: 0 })}
                  >
                    {'<<'}
                  </Button>
                  <Button
                    className="sol-management-pagination-icon-button"
                    aria-label="Previous"
                    disabled={currentPage === 0}
                    onClick={() =>
                      this.setState({ currentPage: currentPage - 1 })
                    }
                  >
                    {'<'}
                  </Button>
                  <span>
                    {' '}
                    {currentPage + 1} - {totalPages}{' '}
                  </span>
                  <Button
                    className="sol-management-pagination-icon-button"
                    aria-label="Next"
                    disabled={currentPage >= totalPages - 1}
                    onClick={() =>
                      this.setState({ currentPage: currentPage + 1 })
                    }
                  >
                    {'>'}
                  </Button>
                  <Button
                    className="sol-management-pagination-icon-button"
                    aria-label="Last"
                    disabled={currentPage >= totalPages - 1}
                    onClick={() =>
                      this.setState({ currentPage: totalPages - 1 })
                    }
                  >
                    {'>>'}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <EmptyContent
              message={
                <FormattedMessage
                  id="DeveloperSubscriptions.subscriptionsEmpty"
                  defaultMessage="No subscriptions yet, Check back soon!"
                />
              }
            />
          )}
        </Card>
      </div>
    );
  }
}

const getSolutionSubscriptions = gql`
  query($solutionId: ID) {
    getSolutionSubscriptions(solutionId: $solutionId) {
      id
      subscriptionId
      solutionId
      entityId
      userId
      status
      isMarketPlaceSubScription
      plan {
        id
        planName
      }
      subscriptionStartDate
      solution {
        id
        developerId
        submittedOn
        overview {
          name
          solutionLogo
        }
        registeredOn
      }
      paymentMode
    }
  }
`;

const getEntitiesQuery = gql`
  query($id: [ID]) {
    subscribedEntitiesFromPlatfom(id: $id) {
      id
      name
      website
      phone
      type
      lastUpdated
      created
      address {
        region
        city
        street
        countryCode
        postalCode
      }
    }
  }
`;

export default withRouter(Subscriptions);
